import { useState, useEffect, useRef } from 'react';
import { useAuthHttp } from '@hooks';
import { LIST_SKUS_URL } from '@constants';
import { AvailableSKU } from '../forms/client/CreateInboundShipmentNoteForm/types';

export type AvailableProduct = {
  productId: string;
  name: string;
  skus: AvailableSKU[];
};

const groupSkusByProduct = (skus: AvailableSKU[]): AvailableProduct[] => {
  const productsById = new Map<string, AvailableProduct>();

  skus.forEach((sku) => {
    const productId = sku.productDetails?.productId;
    if (!productId) return;

    const existing = productsById.get(productId);
    if (existing) {
      existing.skus.push(sku);
      return;
    }

    productsById.set(productId, {
      productId,
      name: sku.productDetails?.name || sku.name,
      skus: [sku],
    });
  });

  return Array.from(productsById.values());
};

export const useAvailableProducts = () => {
  const authHttp = useAuthHttp();

  const [availableProducts, setAvailableProducts] = useState<
    AvailableProduct[]
  >([]);
  const [loadingAvailableProducts, setLoadingAvailableProducts] =
    useState(false);
  const [productsQuery, setProductsQuery] = useState('');
  const previousController = useRef<AbortController>();
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (productsQuery.length > 0 && productsQuery.length < 3) {
      return undefined;
    }

    const searchProducts = async () => {
      if (previousController.current) {
        previousController.current.abort();
      }
      const controller = new AbortController();
      const { signal } = controller;
      previousController.current = controller;

      setLoadingAvailableProducts(true);

      try {
        const response = await authHttp.post(
          LIST_SKUS_URL,
          {
            searchQuery: productsQuery,
            isPalletisingRequired: true,
            pagination: {
              lastItemSort: null,
              itemsPerPage: 50,
            },
          },
          { signal }
        );

        const {
          data: { skus },
        }: { data: { skus: AvailableSKU[] } } = response;

        setAvailableProducts(groupSkusByProduct(skus));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setLoadingAvailableProducts(false);
      }
    };

    debounceTimer.current = setTimeout(searchProducts, 350);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      previousController.current?.abort();
    };
  }, [productsQuery]);

  return {
    availableProducts,
    loadingAvailableProducts,
    productsQuery,
    setProductsQuery,
  };
};
